import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';

import type { BreadcrumbConfig } from '../ui-components/page/PageHeader';

interface PageHeaderState {
  title: string;
  breadcrumbs: BreadcrumbConfig[];
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
}

interface PageHeaderContextValue {
  header: PageHeaderState;
  setActions: (actions?: React.ReactNode) => void;
  setTitle: (title?: string) => void;
  setBreadcrumbs: (breadcrumbs?: BreadcrumbConfig[]) => void;
  setSubtitle: (subtitle?: React.ReactNode) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue>({
  header: { title: '', breadcrumbs: [] },
  setActions: () => undefined,
  setTitle: () => undefined,
  setBreadcrumbs: () => undefined,
  setSubtitle: () => undefined,
});

interface PageHeaderProviderProps {
  title: string;
  breadcrumbs: BreadcrumbConfig[];
  children: React.ReactNode;
}

const areBreadcrumbsEqual = (
  a: BreadcrumbConfig[] | null,
  b: BreadcrumbConfig[] | null
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].label !== b[i].label) return false;
    if ((a[i].to ?? '') !== (b[i].to ?? '')) return false;
    if ((a[i].href ?? '') !== (b[i].href ?? '')) return false;
    if (Boolean(a[i].current) !== Boolean(b[i].current)) return false;
  }
  return true;
};

const normalizeBreadcrumbs = (
  items: BreadcrumbConfig[] | null
): BreadcrumbConfig[] | null => {
  if (!items || items.length === 0) return items;
  return items.map((item, index) => {
    const isLast = index === items.length - 1;
    if (isLast) {
      const resolvedTo =
        item.to ??
        (item.href || item.onClick
          ? undefined
          : typeof globalThis !== 'undefined' && globalThis.location
          ? globalThis.location.pathname
          : undefined);
      return {
        ...item,
        current: true,
        to: resolvedTo,
      };
    }
    return {
      ...item,
      current: false,
    };
  });
};

export const PageHeaderProvider: React.FC<PageHeaderProviderProps> = ({
  title,
  breadcrumbs,
  children,
}) => {
  const [actions, setActionsState] = useState<React.ReactNode>();
  const [subtitle, setSubtitleState] = useState<React.ReactNode>();
  const [titleOverride, setTitleOverride] = useState<string | null>(null);
  const [breadcrumbsOverride, setBreadcrumbsOverride] = useState<
    BreadcrumbConfig[] | null
  >(null);

  const isFirstRender = useRef(true);

  const breadcrumbKey = useMemo(
    () => breadcrumbs.map((item) => item.label).join('|'),
    [breadcrumbs]
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    // setActionsState(undefined); // Removed to prevent race condition with child effects
    setTitleOverride(null);
    setBreadcrumbsOverride(null);
    // setSubtitleState(undefined); // Removed to prevent race condition with child effects
  }, [title, breadcrumbKey]);

  const header = useMemo<PageHeaderState>(
    () => ({
      title: titleOverride ?? title,
      breadcrumbs: breadcrumbsOverride ?? breadcrumbs,
      subtitle: subtitle,
      actions: actions,
    }),
    [title, titleOverride, breadcrumbs, breadcrumbsOverride, subtitle, actions]
  );

  const setActions = useCallback(
    (nextActions?: React.ReactNode) =>
      setActionsState((prev) => (prev === nextActions ? prev : nextActions)),
    []
  );

  const setSubtitle = useCallback(
    (nextSubtitle?: React.ReactNode) =>
      setSubtitleState((prev) => (prev === nextSubtitle ? prev : nextSubtitle)),
    []
  );

  const setTitle = useCallback((nextTitle?: string) => {
    setTitleOverride((prev) => {
      const resolved = nextTitle ?? null;
      return prev === resolved ? prev : resolved;
    });
  }, []);

  const setBreadcrumbs = useCallback((nextBreadcrumbs?: BreadcrumbConfig[]) => {
    setBreadcrumbsOverride((prev) => {
      const resolved = normalizeBreadcrumbs(nextBreadcrumbs ?? null);
      return areBreadcrumbsEqual(prev, resolved) ? prev : resolved;
    });
  }, []);

  const contextValue = useMemo<PageHeaderContextValue>(
    () => ({
      header,
      setActions,
      setTitle,
      setBreadcrumbs,
      setSubtitle,
    }),
    [header, setActions, setTitle, setBreadcrumbs, setSubtitle]
  );

  return (
    <PageHeaderContext.Provider value={contextValue}>
      {children}
    </PageHeaderContext.Provider>
  );
};

export const usePageHeaderContext = (): PageHeaderContextValue =>
  useContext(PageHeaderContext);
